import api from './api';

const visitService = {
  async getAll(projectCode = null, stage = null) {
    const params = {};
    if (projectCode) params.projectCode = projectCode;
    if (stage) params.stage = stage;
    const response = await api.get('/visit', { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/visit/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/visit/create', data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.post(`/visit/${id}`, data);
    return response.data;
  },

  async confirm(id, payload = {}) {
    const response = await api.post(`/visit/${id}/confirm`, payload);
    return response.data;
  },

  async cancel(id, reasonOrPayload) {
    const isPayload = reasonOrPayload != null && typeof reasonOrPayload === 'object' && !Array.isArray(reasonOrPayload);
    const reasonStr = isPayload
      ? (reasonOrPayload.reason != null ? String(reasonOrPayload.reason).trim() : '')
      : (typeof reasonOrPayload === 'string' ? reasonOrPayload.trim() : '');
    const markInactive = isPayload && reasonOrPayload.markInactive === true;
    const body = {
      reason: reasonStr || 'Marked inactive by user',
      ...(markInactive && { markInactive: true }),
    };
    const response = await api.post(`/visit/${id}/cancel`, body);
    return response.data;
  },

  async confirmByToken(id, token, remark) {
    const response = await api.post(`/visit/${id}/confirm-by-token`, { token, remark: remark || undefined });
    return response.data;
  },

  async cancelByToken(id, token, remark) {
    const response = await api.post(`/visit/${id}/cancel-by-token`, { token, remark: remark || undefined });
    return response.data;
  },

  async followUpConfirmByToken(id, token, remark) {
    const response = await api.post(`/visit/${id}/followup-confirm-by-token`, { token, remark: remark || undefined });
    return response.data;
  },

  async followUpRejectByToken(id, token, remark) {
    const response = await api.post(`/visit/${id}/followup-reject-by-token`, { token, remark: remark || undefined });
    return response.data;
  },

  async followUpConfirm(id, payload = {}) {
    const response = await api.post(`/visit/${id}/followup-confirm`, payload);
    return response.data;
  },

  async followUpReject(id, reasonOrPayload) {
    const isPayload = reasonOrPayload != null && typeof reasonOrPayload === 'object' && !Array.isArray(reasonOrPayload);
    const reasonStr = isPayload
      ? (reasonOrPayload.reason != null ? String(reasonOrPayload.reason).trim() : '')
      : (typeof reasonOrPayload === 'string' ? reasonOrPayload.trim() : '');
    const body = { reason: reasonStr || 'Rejected follow-up PI confirmation' };
    const response = await api.post(`/visit/${id}/followup-reject`, body);
    return response.data;
  },

  async getSiteVisits(siteId) {
    const response = await api.get(`/visit/site/${siteId}`);
    return response.data;
  },

  async getOverdue() {
    const response = await api.get('/visit/overdue');
    return response.data;
  },

  async sendConfirmationLetter(id, payload) {
    const response = await api.post(`/visit/${id}/send-confirmation-letter`, payload);
    return response.data;
  },

  async verifyOTP(id, otp) {
    const response = await api.post(`/visit/${id}/verify-otp`, { otp });
    return response.data;
  },

  async sendFollowUpLetter(id, payload) {
    const response = await api.post(`/visit/${id}/send-followup-letter`, payload);
    return response.data;
  },

  async verifyFollowUpOTP(id, otp) {
    const response = await api.post(`/visit/${id}/verify-followup-otp`, { otp });
    return response.data;
  },

  async execute(id, payload) {
    const response = await api.post(`/visit/${id}/execute`, payload);
    return response.data;
  },

  async getAssignedCRFTemplate(visitId) {
    const response = await api.get(`/visit/${visitId}/crf-template`);
    return response.data;
  },

  async getPlanner(projectCode = null) {
    const params = projectCode ? { projectCode } : {};
    const response = await api.get('/visit/planner', { params });
    return response.data;
  },

  /** @param {string} [studyProjectCode] Current project code; required so PI notifications are scoped to that study (nProjectNo on server). */
  async getPINotifications(studyProjectCode) {
    const code = (studyProjectCode || '').toString().trim();
    const params = code ? { projectCode: code } : {};
    const response = await api.get('/visit/pi-notifications', { params });
    return response.data || [];
  },

  /** @param {string} [studyProjectCode] Current project code; required so PI follow-up notifications are scoped to that study. */
  async getPIFollowUpNotifications(studyProjectCode) {
    const code = (studyProjectCode || '').toString().trim();
    const params = code ? { projectCode: code } : {};
    const response = await api.get('/visit/pi-followup-notifications', { params });
    return response.data || [];
  },

  async getUnlockEligibleVisits(projectCode) {
    const code = (projectCode || '').toString().trim();
    const params = code ? { projectCode: code } : {};
    const response = await api.get('/visit/unlock-eligible', { params });
    return response.data || [];
  },

  async unlockVisit(id, remark) {
    const response = await api.post(`/visit/${id}/unlock`, { remark: remark || '' });
    return response.data;
  },

  async getUnlockAuditHistory(visitId) {
    const response = await api.get(`/visit/${visitId}/unlock-audit`);
    return response.data || [];
  },
};

export default visitService;